/**
 * D12b — parse `PRO_TIER_CAP_REACHED:` errors raised by Postgres triggers
 * (see supabase/migrations/20260603170000_d12b_pro_tier_caps.sql) and
 * surface a tier-aware upgrade prompt instead of the raw text.
 *
 * The trigger raises:
 *   RAISE EXCEPTION 'PRO_TIER_CAP_REACHED: customer_db — you have N customers (cap M on the X tier). Upgrade to add more.';
 *
 * Supabase-js surfaces this as `{ message: 'PRO_TIER_CAP_REACHED: …' }`.
 * Wrap any mutating supabase call that *could* trip a cap (customer
 * create, fitting create, offer create, briefing create) in
 * `handleTierCapError` so the SPA shows the prompt + tier label.
 */

import { proTierLabel, type ProTier, normalizeProTier } from './proTier';

export type TierCapKey =
  | 'customer_db'
  | 'fittings_per_month'
  | 'quotes_per_month'
  | 'briefings_per_month';

export interface TierCapError {
  /** Which cap was reached. */
  cap: TierCapKey;
  /** The pro's tier as the trigger saw it. */
  tier: ProTier;
  /** Friendly tier label for the upgrade prompt. */
  tierLabel: string;
  /** Friendly toast message ready to render. */
  message: string;
}

const CAP_LABELS: Record<TierCapKey, string> = {
  customer_db: 'Customer database',
  fittings_per_month: 'Fittings this month',
  quotes_per_month: 'Quotes this month',
  briefings_per_month: 'AI briefings this month',
};

/**
 * Inspect a supabase-js error (or any thrown object) and return a
 * structured TierCapError if it's one of ours. Returns null when the
 * error is unrelated.
 *
 * The trigger message uses an em dash ("—") between sections; we parse
 * it loosely so a future copy edit doesn't break the matcher.
 */
export function parseTierCapError(err: unknown): TierCapError | null {
  const message = readMessage(err);
  if (!message) return null;
  const match = message.match(
    /PRO_TIER_CAP_REACHED:\s*(\w+)\s*[—-]\s*(.*?)\s*\(cap\s+\d+\s+on the\s+(\w+)\s+tier\)/i,
  );
  if (!match) return null;
  const cap = match[1] as TierCapKey;
  if (!(cap in CAP_LABELS)) return null;
  const tier = normalizeProTier(match[3]);
  return {
    cap,
    tier,
    tierLabel: proTierLabel(tier),
    message: `${CAP_LABELS[cap]} cap reached on ${proTierLabel(tier)}. Upgrade to keep going.`,
  };
}

function readMessage(err: unknown): string | null {
  if (typeof err === 'string') return err;
  if (!err || typeof err !== 'object') return null;
  const candidate = err as { message?: unknown; error?: { message?: unknown } };
  if (typeof candidate.message === 'string') return candidate.message;
  if (typeof candidate.error?.message === 'string') return candidate.error.message;
  return null;
}
